import { PageSize } from '@/constant';
import {
	GetSystemUserListParams,
	ISystemUser,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { formatSystemUserList, RootState } from '..';
import {
	IPayloadCreateSystemUserRequest,
	IPayloadDeleteSystemUserRequest,
	IPayloadGetSystemUserDetailRequest,
	IPayloadGetSystemUserDetailSuccess,
	IPayloadGetSystemUserListRequest,
	IPayloadGetSystemUserListSuccess,
	ISystemUserDetailStore,
} from './system-user.type';

const InitParams: GetSystemUserListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	systemUserList: [] as ISystemUserDetailStore[],
	total: 0,
	params: InitParams,
	systemUserDetail: {} as ISystemUserDetailStore,
};

const systemUserSlice = createSlice({
	initialState,
	name: 'systemUser',
	reducers: {
		/**---- GET SYSTEM_USER LIST START ---- */
		getSystemUserListRequest: (
			state,
			action: PayloadAction<IPayloadGetSystemUserListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getSystemUserListSuccess: (
			state,
			action: PayloadAction<IPayloadGetSystemUserListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.systemUserList = content;
			state.total = total;
		},
		getSystemUserListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.systemUserList = [];
		},
		/**---- GET SYSTEM_USER LIST END ---- */

		/**---- CREATE SYSTEM_USER START ---- */
		createSystemUserRequest: (
			state,
			action: PayloadAction<IPayloadCreateSystemUserRequest>
		) => {
			state.fetching = true;
		},
		createSystemUserSuccess: (state) => {
			state.fetching = false;
		},
		createSystemUserFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE SYSTEM_USER END ---- */

		/**---- DELETE SYSTEM_USER START ---- */
		deleteSystemUserRequest: (
			state,
			action: PayloadAction<IPayloadDeleteSystemUserRequest>
		) => {
			state.fetching = true;
		},
		deleteSystemUserSuccess: (state) => {
			state.fetching = false;
		},
		deleteSystemUserFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE SYSTEM_USER START ---- */

		/**---- GET SYSTEM_USER DETAIL START ---- */
		getSystemUserDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetSystemUserDetailRequest>
		) => {
			state.fetching = true;
		},
		getSystemUserDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetSystemUserDetailSuccess>
		) => {
			const { systemUserDetail } = action.payload;
			state.fetching = false;
			state.systemUserDetail = systemUserDetail;
		},
		getSystemUserDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET SYSTEM_USER DETAIL END ---- */

		/**---- GET SYSTEM NOTICE DETAIL START ---- */
		updateSystemUserDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ISystemUser>>
		) => {
			const { data } = action.payload;
			const systemUserDetail = { ...state.systemUserDetail };
			state.systemUserDetail = mergeItemUpdated(systemUserDetail, data);
		},
		/**---- GET SYSTEM NOTICE DETAIL END ---- */
		updateSystemUserSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ISystemUser>>
		) => {
			const { data } = action.payload;
			const systemUserList = [...state.systemUserList];
			state.systemUserList = formatSystemUserList(
				mergeListItemUpdated<ISystemUserDetailStore, ISystemUser>(
					systemUserList,
					data
				)
			);
		},
	},
});

export const SystemUserSelectors = {
	selectFetching: (state: RootState) => state.systemUser.fetching,
	selectParams: (state: RootState) => state.systemUser.params,
	selectList: (state: RootState) => state.systemUser.systemUserList,
	selectTotal: (state: RootState) => state.systemUser.total,
	selectDetail: (state: RootState) => state.systemUser.systemUserDetail,
};

export const SystemUserActions = systemUserSlice.actions;
export const SystemUserReducer = systemUserSlice.reducer;
